import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

export const AdminSiteConfigLimitsSettings = () => {
  const [dailyLimit, setDailyLimit] = useState('300');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLimitConfig();
  }, []);

  const fetchLimitConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('site_configurations')
        .select('value')
        .eq('key', 'daily_generation_limit')
        .single();

      if (error) throw error;
      if (data) {
        setDailyLimit(data.value);
      }
    } catch (error) {
      console.error('Error fetching limit config:', error);
      toast.error('Failed to load limit configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const limitValue = parseInt(dailyLimit);
      if (isNaN(limitValue) || limitValue < 1) {
        toast.error('Please enter a valid number greater than 0');
        return;
      }

      const { error } = await supabase
        .from('site_configurations')
        .update({ value: dailyLimit, updated_at: new Date().toISOString() })
        .eq('key', 'daily_generation_limit');

      if (error) throw error;

      toast.success('Daily generation limit updated successfully');
    } catch (error) {
      console.error('Error saving limit:', error);
      toast.error('Failed to update limit');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card className="bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader>
        <CardTitle>User Generation Limits</CardTitle>
        <CardDescription>
          Configure daily generation limits for users
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="daily-limit">Daily Generation Limit</Label>
          <Input
            id="daily-limit"
            type="number"
            min="1"
            value={dailyLimit}
            onChange={(e) => setDailyLimit(e.target.value)}
            placeholder="300"
          />
          <p className="text-sm text-muted-foreground">
            Maximum number of images a user can generate per day
          </p>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Limit
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
